import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './AuthContext'
import { useOrg } from './OrgContext'
import { callEdgeFunction } from '@/lib/edge-functions'
import type {
  AccountMap,
  AccountMapCompany,
  BuyerPersona,
  PersonaGroup,
  ICPTemplate,
  FeedbackType,
  ICPInsight,
  TierKeywords,
  TierSeniority,
  OutreachStrategy,
} from '@/types/account-mapping'
import type { ICPBuilderData } from '@/types/icp-builder'
import type { EnrichCompanyResponse, ReEvaluateResponse, CompanyEnrichment } from '@/types/enrichment'
import type { CompanyRegistryEntry, RegistryType, ExclusionStats } from '@/types/registry'
import { EXCLUSION_TYPES } from '@/types/registry'
import { normalizeCompanyName } from '@/lib/company-normalize'

// ── Search Sales Navigator types ──

export interface SearchSalesNavigatorParams {
  accountMapId: string
  keywords?: string
  companyNames?: string[]
  titleKeywords?: string[]
  location?: string
  seniority?: string[]
  companySizeMin?: string
  companySizeMax?: string
  limit?: number
  cursor?: string
}

export interface SalesNavResult {
  firstName: string
  lastName: string
  title: string | null
  company: string | null
  linkedinUrl: string | null
  linkedinProviderId: string | null
  headline: string | null
  location: string | null
}

export interface SearchSalesNavigatorResponse {
  success: boolean
  results: SalesNavResult[]
  cursor: string | null
  hasMore: boolean
  total: number
}

// ── Cascade Search Company types ──

export interface CascadeSearchCompanyResponse {
  success: boolean
  totalFound: number
  companyName: string
  personaResults: Array<{
    personaId: string
    personaName: string
    resultsCount: number
    searchLevel: 1 | 2 | 3
    queryUsed: string
    levelDetails: Array<{
      level: 1 | 2 | 3
      label: string
      keywords: string[]
      resultsCount: number
      skipped?: boolean
    }>
    error?: string
  }>
}

// ── Enrich types ──

export interface EnrichProspectResponse {
  success: boolean
  results: Array<{
    prospectId: string
    success: boolean
    email?: string | null
    phone?: string | null
    source?: string
    error?: string
    failReason?: string | null
    emailCreditWarning?: boolean
    phoneCreditWarning?: boolean
  }>
  summary: {
    total: number
    enriched: number
    withEmail: number
    withPhone: number
    emailCreditWarning?: boolean
    phoneCreditWarning?: boolean
    failReasonCounts?: Record<string, number>
  }
}

// ── Discover ICP types ──

export type FitCategory = 'high' | 'medium' | 'low'

export const FIT_CATEGORY_CONFIG: Record<FitCategory, { label: string; variant: 'success' | 'warning' | 'destructive' }> = {
  high: { label: 'High Fit', variant: 'success' },
  medium: { label: 'Medium Fit', variant: 'warning' },
  low: { label: 'Low Fit', variant: 'destructive' },
}

export interface DiscoveredCompany {
  company_name: string
  industry: string | null
  company_size: string | null
  website: string | null
  location: string | null
  description: string | null
  relevance_reason: string | null
  relevance_score: number
  fit_category: FitCategory
  score_breakdown?: Record<string, number>
}

export interface DiscoverICPResponse {
  success: boolean
  companies: DiscoveredCompany[]
  excludedCompanies?: Array<{ company_name: string; reason: string }>
  queriesUsed: string[]
  totalSearchResults: number
  message?: string
}

// ── Suggested Buyer Persona types ──

export interface SuggestedPersona {
  name: string
  title_keywords: string[]
  seniority: string
  department: string
  reasoning: string
  // Adaptive fields (v2)
  description?: string
  role_in_buying_committee?: string
  departments?: string[]
  title_keywords_by_tier?: TierKeywords
  seniority_by_tier?: TierSeniority
}

// ── Context type ──

interface AccountMappingContextType {
  accountMaps: AccountMap[]
  isLoading: boolean
  // Account Maps CRUD
  createAccountMap: (name: string, description?: string) => Promise<AccountMap | null>
  updateAccountMap: (id: string, data: Partial<AccountMap>) => Promise<void>
  deleteAccountMap: (id: string) => Promise<void>
  // ICP polish + discover
  polishICPDescription: (description: string) => Promise<string>
  discoverICPCompanies: (icpDescription: string, min: number, max: number) => Promise<DiscoverICPResponse>
  // Companies CRUD
  addCompany: (company: Omit<AccountMapCompany, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => Promise<AccountMapCompany | null>
  updateCompany: (id: string, data: Partial<AccountMapCompany>) => Promise<void>
  deleteCompany: (id: string) => Promise<void>
  // Buyer Personas CRUD
  addPersona: (persona: Omit<BuyerPersona, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => Promise<BuyerPersona | null>
  updatePersona: (id: string, data: Partial<BuyerPersona>) => Promise<void>
  deletePersona: (id: string) => Promise<void>
  // Persona Groups CRUD
  personaGroups: PersonaGroup[]
  personaGroupsLoading: boolean
  addPersonaGroup: (data: { name: string; description?: string | null; scope: 'personal' | 'organization' }) => Promise<PersonaGroup>
  updatePersonaGroup: (id: string, data: Partial<Pick<PersonaGroup, 'name' | 'description' | 'scope'>>) => Promise<void>
  deletePersonaGroup: (id: string) => Promise<void>
  // Prospects
  saveProspects: (accountMapId: string, companyId: string | null, prospects: SalesNavResult[], options?: { personaId?: string; buyingRole?: string; searchMetadata?: Record<string, unknown> }) => Promise<number>
  saveProspectsBatch: (accountMapId: string, companyId: string | null, prospects: SalesNavResult[], options?: { personaId?: string; buyingRole?: string; searchMetadata?: Record<string, unknown> }) => Promise<number>
  refreshAccountMaps: () => void
  deleteProspect: (id: string) => Promise<void>
  bulkDeleteProspects: (ids: string[]) => Promise<void>
  // Edge function wrappers
  searchSalesNavigator: (params: SearchSalesNavigatorParams) => Promise<SearchSalesNavigatorResponse>
  cascadeSearchCompany: (accountMapId: string, companyId: string, maxPerRole: number) => Promise<CascadeSearchCompanyResponse>
  enrichProspect: (prospectId: string, companyWebsite?: string) => Promise<EnrichProspectResponse>
  bulkEnrichProspects: (prospectIds: string[], companyWebsite?: string, onProgress?: (done: number, total: number) => void) => Promise<EnrichProspectResponse>
  enrichCompanyProspects: (accountMapId: string, companyId: string) => Promise<void>
  findDuplicateProspects: (prospects: Array<{ id: string; linkedin_url: string | null; linkedin_provider_id: string | null; email: string | null; first_name: string; last_name: string; company: string | null }>) => Promise<{ duplicateIds: Set<string>; duplicatesOfLeads: number; duplicatesAmongProspects: number }>
  // Promote to lead
  promoteProspectToLead: (prospectId: string, cadenceId?: string) => Promise<{ leadId: string; duplicate: boolean }>
  bulkPromoteProspects: (prospectIds: string[], cadenceId?: string) => Promise<{ promoted: number; duplicates: number }>
  // ICP Templates
  icpTemplates: ICPTemplate[]
  saveTemplate: (name: string, description: string | null, builderData: ICPBuilderData) => Promise<ICPTemplate | null>
  deleteTemplate: (id: string) => Promise<void>
  // ICP Feedback
  submitFeedback: (accountMapId: string, companyName: string, feedback: FeedbackType, discoveryData?: Record<string, unknown>) => Promise<void>
  deleteFeedback: (accountMapId: string, companyName: string) => Promise<void>
  getFeedbackForMap: (accountMapId: string) => Promise<Record<string, FeedbackType>>
  // Smart ICP Insights
  getSmartICPInsights: (accountMapId: string) => Promise<ICPInsight[]>
  // Buyer Persona Suggestions
  suggestBuyerPersonas: (id: string, source?: 'icpProfileId' | 'personaGroupId') => Promise<SuggestedPersona[]>
  suggestPersonaTitles: (params: { productCategory: string; companyDescription: string; buyingRole: string; personaDescription: string }) => Promise<{ tiers: TierKeywords; seniority: TierSeniority }>
  // Company Enrichment (Firecrawl)
  enrichCompany: (companyName: string, website?: string | null) => Promise<EnrichCompanyResponse>
  reEvaluateCompanies: (icpDescription: string, companies: Array<DiscoveredCompany & { enrichment?: CompanyEnrichment }>) => Promise<ReEvaluateResponse>
  // Company Registry
  companyRegistry: CompanyRegistryEntry[]
  registryLoading: boolean
  addRegistryEntry: (entry: { company_name_display: string; registry_type: RegistryType; source?: string; website?: string | null; industry?: string | null; company_size?: string | null; location?: string | null; exclusion_reason?: string | null }) => Promise<CompanyRegistryEntry | null>
  addRegistryEntries: (entries: Array<{ company_name_display: string; registry_type: RegistryType; source?: string; website?: string | null; industry?: string | null; exclusion_reason?: string | null }>) => Promise<number>
  updateRegistryEntry: (id: string, data: Partial<CompanyRegistryEntry>) => Promise<void>
  deleteRegistryEntry: (id: string) => Promise<void>
  getExclusionNames: () => string[]
  getExclusionStats: () => ExclusionStats
  // AI Prospect Validation
  validateProspects: (accountMapId: string, companyId: string, productDescription: string, productCategory: string, icpDescription?: string) => Promise<{ validated: number; total: number }>
  applyScoreThreshold: (accountMapId: string, companyId: string, minScore: number) => Promise<{ skipped: number }>
  skipProspect: (prospectId: string, reason?: string) => Promise<void>
  unskipProspect: (prospectId: string) => Promise<void>
  // Outreach Strategy
  getOutreachStrategy: (accountMapId: string, companyId: string, productDescription: string, productCategory: string) => Promise<OutreachStrategy | null>
}

const AccountMappingContext = createContext<AccountMappingContextType | undefined>(undefined)

export function AccountMappingProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth()
  const { orgId } = useOrg()
  const queryClient = useQueryClient()

  // ── Queries ──

  const { data: accountMaps = [], isLoading } = useQuery({
    queryKey: ['account-maps', orgId, user?.id],
    queryFn: async () => {
      if (!user || !orgId) return []
      const { data, error } = await supabase
        .from('account_maps')
        .select('*, account_map_companies(*), buyer_personas(*), prospects(*), icp_profiles(*, buyer_personas(*))')
        .eq('org_id', orgId)
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      // Normalize: if map has a linked ICP profile, expose its personas as map.buyer_personas for backward compat
      return (data || []).map((m: Record<string, unknown>) => {
        const profilePersonas = (m.icp_profiles as Record<string, unknown> | null)?.buyer_personas
        return {
          ...m,
          icp_profile: m.icp_profiles || null,
          // If profile is linked and has personas, use those; otherwise keep inline personas
          buyer_personas: (m.icp_profile_id && Array.isArray(profilePersonas) && profilePersonas.length > 0)
            ? profilePersonas
            : m.buyer_personas || [],
        }
      }) as AccountMap[]
    },
    enabled: !!user && !!orgId,
  })

  // ── Account Maps CRUD ──

  const createAccountMapMutation = useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      if (!user || !orgId) throw new Error('Not authenticated')

      // Auto-link ICP profile if the org has exactly one
      let autoIcpProfileId: string | null = null
      const { data: icpProfiles } = await supabase
        .from('icp_profiles')
        .select('id')
        .eq('org_id', orgId)
      if (icpProfiles && icpProfiles.length === 1) {
        autoIcpProfileId = icpProfiles[0].id
      }

      const { data, error } = await supabase
        .from('account_maps')
        .insert({
          owner_id: user.id,
          org_id: orgId,
          name,
          description: description || null,
          icp_profile_id: autoIcpProfileId,
          filters_json: { industry: [], company_size: [], location: [], seniority: [], keywords: [], title_keywords: [] },
        })
        .select()
        .single()
      if (error) throw error
      return data as AccountMap
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-maps'] }),
  })

  const updateAccountMapMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AccountMap> }) => {
      const { error } = await supabase.from('account_maps').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-maps'] }),
  })

  const deleteAccountMapMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('account_maps').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-maps'] }),
  })

  // ── Companies CRUD ──

  const addCompanyMutation = useMutation({
    mutationFn: async (company: Omit<AccountMapCompany, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => {
      if (!orgId) throw new Error('No org selected')
      const { data, error } = await supabase
        .from('account_map_companies')
        .insert({ ...company, org_id: orgId })
        .select()
        .single()
      if (error) throw error
      return data as AccountMapCompany
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-maps'] }),
  })

  const updateCompanyMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AccountMapCompany> }) => {
      const { error } = await supabase.from('account_map_companies').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-maps'] }),
  })

  const deleteCompanyMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('account_map_companies').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-maps'] }),
  })

  // ── Buyer Personas CRUD ──

  const addPersonaMutation = useMutation({
    mutationFn: async (persona: Omit<BuyerPersona, 'id' | 'created_at' | 'updated_at' | 'org_id'>) => {
      if (!orgId) throw new Error('No org selected')
      const { data, error } = await supabase
        .from('buyer_personas')
        .insert({ ...persona, org_id: orgId })
        .select()
        .single()
      if (error) throw error
      return data as BuyerPersona
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-maps'] })
      queryClient.invalidateQueries({ queryKey: ['persona-groups', orgId] })
    },
  })

  const updatePersonaMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<BuyerPersona> }) => {
      const { error } = await supabase.from('buyer_personas').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-maps'] })
      queryClient.invalidateQueries({ queryKey: ['persona-groups', orgId] })
    },
  })

  const deletePersonaMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('buyer_personas').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-maps'] })
      queryClient.invalidateQueries({ queryKey: ['persona-groups', orgId] })
    },
  })

  // ── Persona Groups ──

  const { data: personaGroups = [], isLoading: personaGroupsLoading } = useQuery({
    queryKey: ['persona-groups', orgId],
    queryFn: async () => {
      if (!user || !orgId) return []
      const { data, error } = await supabase
        .from('persona_groups')
        .select('*, buyer_personas(*)')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data || []) as PersonaGroup[]
    },
    enabled: !!user && !!orgId,
  })

  const addPersonaGroupMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string | null; scope: 'personal' | 'organization' }) => {
      if (!user || !orgId) throw new Error('Not authenticated')
      const { data: created, error } = await supabase
        .from('persona_groups')
        .insert({ ...data, owner_id: user.id, org_id: orgId })
        .select()
        .single()
      if (error) throw error
      return created as PersonaGroup
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['persona-groups', orgId] }),
  })

  const updatePersonaGroupMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Pick<PersonaGroup, 'name' | 'description' | 'scope'>> }) => {
      const { error } = await supabase.from('persona_groups').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['persona-groups', orgId] }),
  })

  const deletePersonaGroupMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('persona_groups').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['persona-groups', orgId] }),
  })

  // ── Prospects ──

  const deleteProspectMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('prospects').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-maps'] }),
  })

  // ── ICP Templates ──

  const { data: icpTemplates = [] } = useQuery({
    queryKey: ['icp-templates', orgId],
    queryFn: async () => {
      if (!user || !orgId) return []
      const { data, error } = await supabase
        .from('icp_templates')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data || []) as ICPTemplate[]
    },
    enabled: !!user && !!orgId,
  })

  // ── Company Registry ──

  const { data: companyRegistry = [], isLoading: registryLoading } = useQuery({
    queryKey: ['company-registry', orgId],
    queryFn: async () => {
      if (!user || !orgId) return []
      const { data, error } = await supabase
        .from('company_registry')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
        .limit(5000)
      if (error) throw error
      return (data || []) as CompanyRegistryEntry[]
    },
    enabled: !!user && !!orgId,
  })

  const addRegistryEntryMutation = useMutation({
    mutationFn: async (entry: { company_name_display: string; registry_type: RegistryType; source?: string; website?: string | null; industry?: string | null; company_size?: string | null; location?: string | null; exclusion_reason?: string | null }) => {
      if (!user || !orgId) throw new Error('Not authenticated')
      const normalized = normalizeCompanyName(entry.company_name_display)
      if (!normalized) throw new Error('Company name is required')
      const { data, error } = await supabase
        .from('company_registry')
        .upsert({
          owner_id: user.id,
          org_id: orgId,
          company_name: normalized,
          company_name_display: entry.company_name_display.trim(),
          registry_type: entry.registry_type,
          source: entry.source || 'manual',
          website: entry.website || null,
          industry: entry.industry || null,
          company_size: entry.company_size || null,
          location: entry.location || null,
          exclusion_reason: entry.exclusion_reason || null,
        }, { onConflict: 'org_id,company_name' })
        .select()
        .single()
      if (error) throw error
      return data as CompanyRegistryEntry
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-registry'] }),
  })

  const updateRegistryEntryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CompanyRegistryEntry> }) => {
      const { error } = await supabase.from('company_registry').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-registry'] }),
  })

  const deleteRegistryEntryMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('company_registry').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-registry'] }),
  })

  const saveTemplateMutation = useMutation({
    mutationFn: async ({ name, description, builderData }: { name: string; description: string | null; builderData: ICPBuilderData }) => {
      if (!user || !orgId) throw new Error('Not authenticated')
      const { data, error } = await supabase
        .from('icp_templates')
        .insert({ owner_id: user.id, org_id: orgId, name, description, builder_data: builderData })
        .select()
        .single()
      if (error) throw error
      return data as ICPTemplate
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['icp-templates'] }),
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('icp_templates').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['icp-templates'] }),
  })

  const chunkEnrich = async (
    prospectIds: string[],
    companyWebsite: string | undefined,
    token: string,
    onProgress?: (done: number, total: number) => void,
  ): Promise<EnrichProspectResponse> => {
    const CHUNK_SIZE = 3
    const allResults: EnrichProspectResponse['results'] = []
    let totalEnriched = 0
    let totalWithPhone = 0
    const failReasonCounts: Record<string, number> = {}
    let anyEmailCreditWarning = false
    let anyPhoneCreditWarning = false

    for (let i = 0; i < prospectIds.length; i += CHUNK_SIZE) {
      const chunk = prospectIds.slice(i, i + CHUNK_SIZE)
      try {
        const chunkResult = await callEdgeFunction<EnrichProspectResponse>(
          'enrich-prospect',
          { prospectIds: chunk, companyWebsite },
          token,
          { timeoutMs: 50000 },
        )
        allResults.push(...(chunkResult.results || []))
        totalEnriched += chunkResult.summary?.enriched || 0
        totalWithPhone += chunkResult.summary?.withPhone || 0
        if (chunkResult.summary?.emailCreditWarning) anyEmailCreditWarning = true
        if (chunkResult.summary?.phoneCreditWarning) anyPhoneCreditWarning = true
        // Accumulate fail reason counts
        for (const [reason, count] of Object.entries(chunkResult.summary?.failReasonCounts || {})) {
          failReasonCounts[reason] = (failReasonCounts[reason] || 0) + count
        }
      } catch (err) {
        for (const pid of chunk) {
          allResults.push({ prospectId: pid, success: false, error: err instanceof Error ? err.message : 'Chunk failed', failReason: 'request_error' })
          failReasonCounts['request_error'] = (failReasonCounts['request_error'] || 0) + 1
        }
      }
      onProgress?.(Math.min(i + CHUNK_SIZE, prospectIds.length), prospectIds.length)
    }

    queryClient.invalidateQueries({ queryKey: ['account-maps'] })
    return {
      success: true,
      results: allResults,
      summary: {
        total: prospectIds.length,
        enriched: totalEnriched,
        withEmail: totalEnriched,
        withPhone: totalWithPhone,
        emailCreditWarning: anyEmailCreditWarning,
        phoneCreditWarning: anyPhoneCreditWarning,
        failReasonCounts,
      },
    }
  }

  return (
    <AccountMappingContext.Provider
      value={{
        accountMaps,
        isLoading,

        // Account Maps
        createAccountMap: async (name, description) =>
          createAccountMapMutation.mutateAsync({ name, description }),
        updateAccountMap: async (id, data) =>
          updateAccountMapMutation.mutateAsync({ id, data }),
        deleteAccountMap: async (id) =>
          deleteAccountMapMutation.mutateAsync(id),

        // Polish ICP description
        polishICPDescription: async (description) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          const result = await callEdgeFunction<{ success: boolean; polishedPrompt: string }>(
            'ai-polish-prompt',
            { description, promptType: 'icp', language: 'es' },
            session.access_token
          )
          return result.polishedPrompt
        },

        // Discover ICP companies (with exclusion filtering)
        discoverICPCompanies: async (icpDescription, min, max) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          // Gather exclusion names from registry (customers, competitors, DNC)
          const excludedCompanyNames = companyRegistry
            .filter(e => (EXCLUSION_TYPES as string[]).includes(e.registry_type))
            .map(e => e.company_name_display)

          // Also exclude all companies synced from Salesforce (active pipeline or won deals)
          if (orgId) {
            const { data: sfAccounts } = await supabase
              .from('salesforce_accounts')
              .select('name')
              .eq('org_id', orgId)
            if (sfAccounts) {
              for (const acc of sfAccounts) {
                if (!excludedCompanyNames.some(n => n.toLowerCase() === acc.name.toLowerCase())) {
                  excludedCompanyNames.push(acc.name)
                }
              }
            }
          }

          return callEdgeFunction<DiscoverICPResponse>(
            'discover-icp-companies',
            { icpDescription, minCompanies: min, maxCompanies: max, excludedCompanies: excludedCompanyNames },
            session.access_token,
            { timeoutMs: 120000 } // 2 min — LLM + Firecrawl searches take time
          )
        },

        // Companies
        addCompany: async (company) =>
          addCompanyMutation.mutateAsync(company),
        updateCompany: async (id, data) =>
          updateCompanyMutation.mutateAsync({ id, data }),
        deleteCompany: async (id) =>
          deleteCompanyMutation.mutateAsync(id),

        // Personas
        addPersona: async (persona) =>
          addPersonaMutation.mutateAsync(persona),
        updatePersona: async (id, data) =>
          updatePersonaMutation.mutateAsync({ id, data }),
        deletePersona: async (id) =>
          deletePersonaMutation.mutateAsync(id),

        // Persona Groups
        personaGroups,
        personaGroupsLoading,
        addPersonaGroup: async (data) =>
          addPersonaGroupMutation.mutateAsync(data),
        updatePersonaGroup: async (id, data) =>
          updatePersonaGroupMutation.mutateAsync({ id, data }),
        deletePersonaGroup: async (id) =>
          deletePersonaGroupMutation.mutateAsync(id),

        // Prospects: bulk save from search results
        saveProspects: async (accountMapId, companyId, prospects, options) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const rows = prospects.map((p) => ({
            account_map_id: accountMapId,
            company_id: companyId,
            owner_id: user.id,
            org_id: orgId,
            first_name: p.firstName,
            last_name: p.lastName,
            title: p.title,
            company: p.company,
            linkedin_url: p.linkedinUrl,
            linkedin_provider_id: p.linkedinProviderId,
            headline: p.headline,
            location: p.location,
            source: 'sales_navigator' as const,
            status: 'new' as const,
            persona_id: options?.personaId || null,
            buying_role: options?.buyingRole || null,
            search_metadata: options?.searchMetadata || null,
          }))
          const { error } = await supabase.from('prospects').insert(rows)
          if (error) throw error
          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
          return rows.length
        },

        // Batch variant: saves without invalidating React Query (for batch loop performance)
        saveProspectsBatch: async (accountMapId, companyId, prospects, options) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const rows = prospects.map((p) => ({
            account_map_id: accountMapId,
            company_id: companyId,
            owner_id: user.id,
            org_id: orgId,
            first_name: p.firstName,
            last_name: p.lastName,
            title: p.title,
            company: p.company,
            linkedin_url: p.linkedinUrl,
            linkedin_provider_id: p.linkedinProviderId,
            headline: p.headline,
            location: p.location,
            source: 'sales_navigator' as const,
            status: 'new' as const,
            persona_id: options?.personaId || null,
            buying_role: options?.buyingRole || null,
            search_metadata: options?.searchMetadata || null,
          }))
          const { error } = await supabase.from('prospects').insert(rows)
          if (error) throw error
          return rows.length
        },

        // Manual refresh after batch operations
        refreshAccountMaps: () => {
          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
        },

        deleteProspect: async (id) =>
          deleteProspectMutation.mutateAsync(id),

        bulkDeleteProspects: async (ids) => {
          if (ids.length === 0) return
          // Supabase .in() supports up to ~300 items; batch in chunks of 200
          const CHUNK = 200
          for (let i = 0; i < ids.length; i += CHUNK) {
            const chunk = ids.slice(i, i + CHUNK)
            const { error } = await supabase.from('prospects').delete().in('id', chunk)
            if (error) throw error
          }
          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
        },

        // Edge function: Sales Navigator search
        searchSalesNavigator: async (params) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          return callEdgeFunction<SearchSalesNavigatorResponse>(
            'search-sales-navigator',
            {
              accountMapId: params.accountMapId,
              keywords: params.keywords,
              companyNames: params.companyNames,
              titleKeywords: params.titleKeywords,
              location: params.location,
              seniority: params.seniority,
              companySizeMin: params.companySizeMin,
              companySizeMax: params.companySizeMax,
              limit: params.limit,
              cursor: params.cursor,
            },
            session.access_token
          )
        },

        // Edge function: Server-side cascade search for one company (all personas)
        cascadeSearchCompany: async (accountMapId, companyId, maxPerRole) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          return callEdgeFunction<CascadeSearchCompanyResponse>(
            'cascade-search-company',
            { accountMapId, companyId, maxPerRole },
            session.access_token,
            { timeoutMs: 120000 }, // 2 min — server does all personas sequentially
          )
        },

        // Edge function: Enrich prospect (Apollo + Firecrawl fallback)
        enrichProspect: async (prospectId, companyWebsite) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          const result = await callEdgeFunction<EnrichProspectResponse>(
            'enrich-prospect',
            { prospectId, companyWebsite },
            session.access_token,
            { timeoutMs: 30000 },
          )
          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
          return result
        },

        // Edge function: Bulk enrich prospects
        bulkEnrichProspects: async (prospectIds, companyWebsite, onProgress?) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          return chunkEnrich(prospectIds, companyWebsite, session.access_token, onProgress)
        },

        // Enrich all un-enriched prospects for a company (used by batch search auto-enrich)
        enrichCompanyProspects: async (accountMapId, companyId) => {
          if (!session?.access_token || !orgId) throw new Error('Not authenticated')
          // Query prospects for this company that don't have email yet
          const { data: prospects } = await supabase
            .from('prospects')
            .select('id')
            .eq('account_map_id', accountMapId)
            .eq('company_id', companyId)
            .eq('org_id', orgId)
            .is('email', null)
          if (!prospects || prospects.length === 0) return
          const ids = prospects.map((p: { id: string }) => p.id)
          await chunkEnrich(ids, undefined, session.access_token)
        },

        // Find duplicate prospects among themselves (same person appearing multiple times).
        // Groups by identity, keeps the copy with the most data, marks the rest.
        findDuplicateProspects: async (prospects) => {
          if (!orgId) throw new Error('Not authenticated')
          const duplicateIds = new Set<string>()
          let duplicatesAmongProspects = 0

          // Score: prefer prospects with more contact data
          const dataScore = (p: typeof prospects[number]) => {
            let s = 0
            if (p.email) s += 2
            if (p.linkedin_url) s += 1
            return s
          }

          // Build identity groups using Union-Find so one prospect can link groups
          // e.g. prospect A has same linkedin_url as B, and B has same email as C → all 3 grouped
          const parent = new Map<string, string>() // prospect id → root id
          const find = (id: string): string => {
            let root = id
            while (parent.get(root) !== root) root = parent.get(root)!
            // Path compression
            let cur = id
            while (cur !== root) { const next = parent.get(cur)!; parent.set(cur, root); cur = next }
            return root
          }
          const union = (a: string, b: string) => {
            const ra = find(a), rb = find(b)
            if (ra !== rb) parent.set(ra, rb)
          }

          // Initialize each prospect as its own root
          for (const p of prospects) parent.set(p.id, p.id)

          // Index by identity keys and union matches
          const byProvId = new Map<string, string>() // linkedin_provider_id → first prospect id
          const byUrl = new Map<string, string>()    // linkedin_url → first prospect id
          const byEmail = new Map<string, string>()   // email (lower) → first prospect id
          const byName = new Map<string, string>()    // "first|last|company" → first prospect id

          for (const p of prospects) {
            // linkedin_provider_id
            if (p.linkedin_provider_id) {
              const existing = byProvId.get(p.linkedin_provider_id)
              if (existing) { union(p.id, existing) } else { byProvId.set(p.linkedin_provider_id, p.id) }
            }
            // linkedin_url
            if (p.linkedin_url) {
              const normalized = p.linkedin_url.replace(/\/+$/, '').toLowerCase()
              const existing = byUrl.get(normalized)
              if (existing) { union(p.id, existing) } else { byUrl.set(normalized, p.id) }
            }
            // email
            if (p.email) {
              const emailLower = p.email.toLowerCase()
              const existing = byEmail.get(emailLower)
              if (existing) { union(p.id, existing) } else { byEmail.set(emailLower, p.id) }
            }
            // first + last + company (only if we have all three, for catching remaining dups)
            if (p.first_name && p.last_name && p.company) {
              const nameKey = `${p.first_name.toLowerCase()}|${p.last_name.toLowerCase()}|${p.company.toLowerCase()}`
              const existing = byName.get(nameKey)
              if (existing) { union(p.id, existing) } else { byName.set(nameKey, p.id) }
            }
          }

          // Collect groups
          const groups = new Map<string, typeof prospects>() // root → members
          for (const p of prospects) {
            const root = find(p.id)
            if (!groups.has(root)) groups.set(root, [])
            groups.get(root)!.push(p)
          }

          // For each group with >1 member: keep the best, mark rest as duplicates
          for (const members of groups.values()) {
            if (members.length <= 1) continue
            // Sort: highest data score first
            members.sort((a, b) => dataScore(b) - dataScore(a))
            // Keep first (best), mark rest
            for (let i = 1; i < members.length; i++) {
              duplicateIds.add(members[i].id)
              duplicatesAmongProspects++
            }
          }

          return { duplicateIds, duplicatesOfLeads: 0, duplicatesAmongProspects }
        },

        // Promote prospect → lead
        promoteProspectToLead: async (prospectId, cadenceId) => {
          if (!user || !orgId) throw new Error('Not authenticated')

          // Fetch the prospect
          const { data: prospect, error: pErr } = await supabase
            .from('prospects')
            .select('*')
            .eq('id', prospectId)
            .eq('org_id', orgId)
            .single()
          if (pErr || !prospect) throw new Error('Prospect not found')

          // Check for duplicate leads (same linkedin_url or email)
          let duplicate = false
          if (prospect.linkedin_url) {
            const { data: existing } = await supabase
              .from('leads')
              .select('id')
              .eq('org_id', orgId)
              .eq('linkedin_url', prospect.linkedin_url)
              .limit(1)
            if (existing && existing.length > 0) duplicate = true
          }
          if (!duplicate && prospect.email) {
            const { data: existing } = await supabase
              .from('leads')
              .select('id')
              .eq('org_id', orgId)
              .eq('email', prospect.email)
              .limit(1)
            if (existing && existing.length > 0) duplicate = true
          }

          // Insert into leads (include linkedin_provider_id so LinkedIn actions work without profile lookup)
          const { data: newLead, error: leadErr } = await supabase
            .from('leads')
            .insert({
              owner_id: user.id,
              org_id: orgId,
              first_name: prospect.first_name,
              last_name: prospect.last_name,
              email: prospect.email,
              phone: prospect.phone,
              title: prospect.title,
              company: prospect.company,
              linkedin_url: prospect.linkedin_url,
              linkedin_provider_id: prospect.linkedin_provider_id,
            })
            .select('id')
            .single()
          if (leadErr) throw leadErr

          // Update prospect status
          await supabase
            .from('prospects')
            .update({ status: 'promoted', promoted_lead_id: newLead.id })
            .eq('id', prospectId)

          // If cadenceId provided, assign lead to cadence
          if (cadenceId) {
            const { data: cadence } = await supabase
              .from('cadences')
              .select('*, cadence_steps(*)')
              .eq('id', cadenceId)
              .single()

            if (cadence) {
              const steps = (cadence.cadence_steps || []) as Array<{ id: string; day_offset: number; order_in_day: number }>
              const sorted = [...steps].sort((a, b) => {
                if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
                return a.order_in_day - b.order_in_day
              })
              const firstStepId = sorted[0]?.id || null

              const { error: memberErr } = await supabase.from('cadence_leads').insert({
                lead_id: newLead.id,
                cadence_id: cadenceId,
                owner_id: user.id,
                org_id: orgId,
                current_step_id: firstStepId,
                status: 'active',
              })

              // Automated cadences are driven ONLY by schedules: process-queue
              // never looks at cadence_leads alone, so a membership without a
              // schedule row is a silent dead end (lead sits on step 1 forever).
              // Same row shape as CadenceContext.assignLeadToCadence (BUG-079);
              // advanceLeadToNextStep chains every following step after execution.
              if (!memberErr && cadence.automation_mode === 'automated' && firstStepId) {
                const { error: schedErr } = await supabase.from('schedules').insert({
                  cadence_id: cadenceId,
                  cadence_step_id: firstStepId,
                  lead_id: newLead.id,
                  owner_id: user.id,
                  org_id: orgId,
                  scheduled_at: new Date(Date.now() + 60_000).toISOString(),
                  timezone: 'UTC',
                  status: 'scheduled',
                })

                // 23505 = partial unique index idx_schedules_active_lead_step (mig
                // 047): an active schedule for this (step, lead) already exists — done.
                if (schedErr && schedErr.code !== '23505') throw schedErr
              }
            }
          }

          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-schedules'] })
          queryClient.invalidateQueries({ queryKey: ['outreach-scheduled'] })

          return { leadId: newLead.id, duplicate }
        },

        // Bulk promote — batched to avoid N×7 sequential queries
        bulkPromoteProspects: async (prospectIds, cadenceId) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const CHUNK = 100

          // 1. Fetch all prospects in one query
          const { data: allProspects, error: pErr } = await supabase
            .from('prospects')
            .select('*')
            .in('id', prospectIds)
            .eq('org_id', orgId)
          if (pErr) throw pErr

          const toPromote = (allProspects || []).filter((p) => p.status !== 'promoted')
          if (toPromote.length === 0) return { promoted: 0, duplicates: 0 }

          // 2. Bulk duplicate-check (2 parallel queries)
          const linkedinUrls = toPromote.map((p) => p.linkedin_url).filter(Boolean) as string[]
          const emails = toPromote.map((p) => p.email).filter(Boolean) as string[]

          const [{ data: existingByLinkedin }, { data: existingByEmail }] = await Promise.all([
            linkedinUrls.length > 0
              ? supabase.from('leads').select('linkedin_url').eq('org_id', orgId).in('linkedin_url', linkedinUrls)
              : Promise.resolve({ data: [] as { linkedin_url: string | null }[] }),
            emails.length > 0
              ? supabase.from('leads').select('email').eq('org_id', orgId).in('email', emails)
              : Promise.resolve({ data: [] as { email: string | null }[] }),
          ])

          const dupLinkedinSet = new Set((existingByLinkedin || []).map((l) => l.linkedin_url).filter(Boolean) as string[])
          const dupEmailSet = new Set((existingByEmail || []).map((l) => l.email).filter(Boolean) as string[])

          // 3. Fetch cadence + first step once
          let firstStepId: string | null = null
          let automationMode: string | null = null
          if (cadenceId) {
            const { data: cadence } = await supabase
              .from('cadences')
              .select('*, cadence_steps(*)')
              .eq('id', cadenceId)
              .single()
            if (cadence) {
              const steps = (cadence.cadence_steps || []) as Array<{ id: string; day_offset: number; order_in_day: number }>
              const sorted = [...steps].sort((a, b) =>
                a.day_offset !== b.day_offset ? a.day_offset - b.day_offset : a.order_in_day - b.order_in_day
              )
              firstStepId = sorted[0]?.id || null
              automationMode = (cadence.automation_mode as string | null) ?? null
            }
          }

          // 4. Batch insert all new leads in chunks
          const leadsPayload = toPromote.map((p) => ({
            owner_id: user.id,
            org_id: orgId,
            first_name: p.first_name,
            last_name: p.last_name,
            email: p.email,
            phone: p.phone,
            title: p.title,
            company: p.company,
            linkedin_url: p.linkedin_url,
          }))

          const newLeadIds: string[] = []
          for (let i = 0; i < leadsPayload.length; i += CHUNK) {
            const chunk = leadsPayload.slice(i, i + CHUNK)
            const { data: inserted, error: leadsErr } = await supabase
              .from('leads')
              .insert(chunk)
              .select('id')
            if (leadsErr) throw leadsErr
            newLeadIds.push(...(inserted || []).map((l: { id: string }) => l.id))
          }

          // 5. Batch upsert prospects → promoted (chunks of 100)
          const prospectUpserts = toPromote.map((p, i) => ({
            id: p.id,
            status: 'promoted' as const,
            promoted_lead_id: newLeadIds[i] || null,
          }))
          for (let i = 0; i < prospectUpserts.length; i += CHUNK) {
            await supabase.from('prospects').upsert(prospectUpserts.slice(i, i + CHUNK))
          }

          // 6. Batch insert cadence_leads (chunks of 100)
          if (cadenceId && newLeadIds.length > 0) {
            const cadenceLeadsPayload = newLeadIds.map((leadId) => ({
              lead_id: leadId,
              cadence_id: cadenceId,
              owner_id: user.id,
              org_id: orgId,
              current_step_id: firstStepId,
              status: 'active',
            }))
            const memberedLeadIds: string[] = []
            for (let i = 0; i < cadenceLeadsPayload.length; i += CHUNK) {
              const chunk = cadenceLeadsPayload.slice(i, i + CHUNK)
              const { error: memberErr } = await supabase.from('cadence_leads').insert(chunk)
              if (!memberErr) memberedLeadIds.push(...chunk.map((r) => r.lead_id))
            }

            // Automated cadences are driven ONLY by schedules: process-queue never
            // looks at cadence_leads alone, so memberships without a schedule row
            // are silent dead ends (leads sit "active" on step 1 forever). Same
            // row shape as CadenceContext.assignLeadToCadence (BUG-079), with the
            // 10s per-lead stagger StartAutomationDialog uses for rate-limiting.
            // advanceLeadToNextStep chains each following step after execution.
            if (automationMode === 'automated' && firstStepId && memberedLeadIds.length > 0) {
              const base = Date.now() + 60_000
              const schedulesPayload = memberedLeadIds.map((leadId, index) => ({
                cadence_id: cadenceId,
                cadence_step_id: firstStepId,
                lead_id: leadId,
                owner_id: user.id,
                org_id: orgId,
                scheduled_at: new Date(base + index * 10_000).toISOString(),
                timezone: 'UTC',
                status: 'scheduled',
              }))
              for (let i = 0; i < schedulesPayload.length; i += CHUNK) {
                const { error: schedErr } = await supabase
                  .from('schedules')
                  .insert(schedulesPayload.slice(i, i + CHUNK))
                // 23505 = partial unique index idx_schedules_active_lead_step (mig
                // 047) — impossible for freshly-inserted lead ids, but treat as done.
                if (schedErr && schedErr.code !== '23505') throw schedErr
              }
            }
          }

          // Count duplicates
          let duplicates = 0
          for (const p of toPromote) {
            if (
              (p.linkedin_url && dupLinkedinSet.has(p.linkedin_url)) ||
              (p.email && dupEmailSet.has(p.email))
            ) duplicates++
          }

          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['cadence-schedules'] })
          queryClient.invalidateQueries({ queryKey: ['outreach-scheduled'] })

          return { promoted: toPromote.length, duplicates }
        },

        // ICP Templates
        icpTemplates,
        saveTemplate: async (name, description, builderData) =>
          saveTemplateMutation.mutateAsync({ name, description, builderData }),
        deleteTemplate: async (id) =>
          deleteTemplateMutation.mutateAsync(id),

        // ICP Feedback
        submitFeedback: async (accountMapId, companyName, feedback, discoveryData) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const { error } = await supabase
            .from('icp_discovery_feedback')
            .upsert(
              {
                account_map_id: accountMapId,
                owner_id: user.id,
                org_id: orgId,
                company_name: companyName,
                feedback,
                discovery_data: discoveryData || null,
              },
              { onConflict: 'account_map_id,company_name,org_id' }
            )
          if (error) throw error
        },

        deleteFeedback: async (accountMapId, companyName) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const { error } = await supabase
            .from('icp_discovery_feedback')
            .delete()
            .eq('account_map_id', accountMapId)
            .eq('company_name', companyName)
            .eq('org_id', orgId)
          if (error) throw error
        },

        getFeedbackForMap: async (accountMapId) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const { data, error } = await supabase
            .from('icp_discovery_feedback')
            .select('company_name, feedback')
            .eq('account_map_id', accountMapId)
            .eq('org_id', orgId)
          if (error) throw error
          const map: Record<string, FeedbackType> = {}
          for (const row of data || []) {
            map[row.company_name] = row.feedback as FeedbackType
          }
          return map
        },

        // Smart ICP Insights
        getSmartICPInsights: async (accountMapId) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          const result = await callEdgeFunction<{ success: boolean; insights: ICPInsight[] }>(
            'analyze-icp-feedback',
            { accountMapId },
            session.access_token
          )
          return result.insights
        },

        // Buyer Persona Suggestions
        suggestBuyerPersonas: async (id, source = 'icpProfileId') => {
          if (!session?.access_token) throw new Error('Not authenticated')
          const result = await callEdgeFunction<{ success: boolean; personas: SuggestedPersona[] }>(
            'suggest-buyer-personas',
            { [source]: id },
            session.access_token
          )
          return result.personas
        },

        suggestPersonaTitles: async (params) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          return callEdgeFunction<{ success: boolean; tiers: TierKeywords; seniority: TierSeniority }>(
            'suggest-persona-titles',
            params,
            session.access_token
          )
        },

        // Company Enrichment (Firecrawl)
        enrichCompany: async (companyName, website) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          return callEdgeFunction<EnrichCompanyResponse>(
            'enrich-company',
            { companyName, website },
            session.access_token
          )
        },
        reEvaluateCompanies: async (icpDescription, companies) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          return callEdgeFunction<ReEvaluateResponse>(
            're-evaluate-companies',
            { icpDescription, companies },
            session.access_token
          )
        },

        // AI Prospect Validation
        validateProspects: async (accountMapId, companyId, productDescription, productCategory, icpDescription?) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          const result = await callEdgeFunction<{ success: boolean; validated: number; total: number }>(
            'validate-prospects',
            { accountMapId, companyId, productDescription, productCategory, icpDescription },
            session.access_token
          )
          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
          return { validated: result.validated, total: result.total }
        },
        applyScoreThreshold: async (accountMapId, companyId, minScore) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const { data: lowScoreProspects, error: fetchErr } = await supabase
            .from('prospects')
            .select('id')
            .eq('account_map_id', accountMapId)
            .eq('company_id', companyId)
            .eq('org_id', orgId)
            .eq('ai_validated', true)
            .lt('relevance_score', minScore)
            .neq('skipped', true)
          if (fetchErr) throw fetchErr
          if (!lowScoreProspects || lowScoreProspects.length === 0) return { skipped: 0 }
          const ids = lowScoreProspects.map(p => p.id)
          const { error: updateErr } = await supabase
            .from('prospects')
            .update({ skipped: true, skip_reason: `Score < ${minScore} (AI validation)` })
            .in('id', ids)
            .eq('org_id', orgId)
          if (updateErr) throw updateErr
          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
          return { skipped: ids.length }
        },
        skipProspect: async (prospectId, reason) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const { error } = await supabase
            .from('prospects')
            .update({ skipped: true, skip_reason: reason || null })
            .eq('id', prospectId)
            .eq('org_id', orgId)
          if (error) throw error
          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
        },
        unskipProspect: async (prospectId) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          const { error } = await supabase
            .from('prospects')
            .update({ skipped: false, skip_reason: null })
            .eq('id', prospectId)
            .eq('org_id', orgId)
          if (error) throw error
          queryClient.invalidateQueries({ queryKey: ['account-maps'] })
        },
        // Outreach Strategy
        getOutreachStrategy: async (accountMapId, companyId, productDescription, productCategory) => {
          if (!session?.access_token) throw new Error('Not authenticated')
          const result = await callEdgeFunction<{ success: boolean; strategy: OutreachStrategy | null }>(
            'suggest-outreach-strategy',
            { accountMapId, companyId, productDescription, productCategory },
            session.access_token
          )
          return result.strategy || null
        },

        // Company Registry
        companyRegistry,
        registryLoading,
        addRegistryEntry: async (entry) =>
          addRegistryEntryMutation.mutateAsync(entry),
        addRegistryEntries: async (entries) => {
          if (!user || !orgId) throw new Error('Not authenticated')
          // Deduplicate by normalized company_name to prevent ON CONFLICT duplicate row error
          const seenNames = new Set<string>()
          const rows = entries.reduce<Array<{
            owner_id: string; org_id: string; company_name: string; company_name_display: string;
            registry_type: string; source: string; website: string | null; industry: string | null;
            exclusion_reason: string | null;
          }>>((acc, e) => {
            const normalized = normalizeCompanyName(e.company_name_display)
            if (!normalized || seenNames.has(normalized)) return acc
            seenNames.add(normalized)
            acc.push({
              owner_id: user.id,
              org_id: orgId,
              company_name: normalized,
              company_name_display: e.company_name_display.trim(),
              registry_type: e.registry_type,
              source: e.source || 'csv_import',
              website: e.website || null,
              industry: e.industry || null,
              exclusion_reason: e.exclusion_reason || null,
            })
            return acc
          }, [])
          // Batch in chunks of 500 to avoid request size limits
          const CHUNK = 500
          for (let i = 0; i < rows.length; i += CHUNK) {
            const chunk = rows.slice(i, i + CHUNK)
            const { error } = await supabase
              .from('company_registry')
              .upsert(chunk, { onConflict: 'org_id,company_name' })
            if (error) throw error
          }
          queryClient.invalidateQueries({ queryKey: ['company-registry'] })
          return rows.length
        },
        updateRegistryEntry: async (id, data) =>
          updateRegistryEntryMutation.mutateAsync({ id, data }),
        deleteRegistryEntry: async (id) =>
          deleteRegistryEntryMutation.mutateAsync(id),
        getExclusionNames: () =>
          companyRegistry
            .filter(e => (EXCLUSION_TYPES as string[]).includes(e.registry_type))
            .map(e => e.company_name_display),
        getExclusionStats: () => {
          const exclusions = companyRegistry.filter(e => (EXCLUSION_TYPES as string[]).includes(e.registry_type))
          const byType: Partial<Record<RegistryType, number>> = {}
          for (const e of exclusions) {
            byType[e.registry_type] = (byType[e.registry_type] || 0) + 1
          }
          return { total: exclusions.length, byType }
        },
      }}
    >
      {children}
    </AccountMappingContext.Provider>
  )
}

export function useAccountMapping() {
  const context = useContext(AccountMappingContext)
  if (context === undefined) {
    throw new Error('useAccountMapping must be used within an AccountMappingProvider')
  }
  return context
}
